
import { useState, useEffect } from "react";
import { Users, Calendar, User, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface MenteeDashboardActionsProps {
  onNavigate: (route: string) => void;
}

const ActionCard = ({ 
  icon, 
  title, 
  route, 
  onNavigate, 
  badge = null 
}: { 
  icon: React.ReactNode, 
  title: string, 
  route: string, 
  onNavigate: (route: string) => void,
  badge?: React.ReactNode | null
}) => (
  <div 
    className="bg-white rounded-lg shadow-sm border p-3 flex flex-col items-center hover:shadow-lg hover:bg-gray-50/50 transition-all duration-300 cursor-pointer min-h-[110px] sm:min-h-[120px] justify-center group" 
    onClick={() => onNavigate(route)}
  >
    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 group-hover:bg-blue-200 transition-colors duration-300 flex items-center justify-center mb-3 relative">
      {icon}
      {badge}
    </div>
    <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base text-center leading-tight">{title}</h3>
    <Button variant="ghost" size="sm" className="text-emu-blue hover:text-emu-blue/80 p-1 group-hover:bg-blue-50 transition-colors duration-200">
      <ArrowRight size={16} className="sm:w-4 sm:h-4" />
    </Button>
  </div>
);

const MenteeDashboardActions = ({ onNavigate }: MenteeDashboardActionsProps) => {
  const { user } = useAuth();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  useEffect(() => {
    if (!user?.id) return;
    
    const fetchUnreadMessageCount = async () => {
      try {
        // First, get all conversations where the user is the mentee
        const { data: conversationsData, error: conversationsError } = await supabase
          .from('conversations')
          .select('id')
          .eq('mentee_id', user.id);
        
        if (conversationsError || !conversationsData) {
          console.error("Error fetching conversations:", conversationsError);
          return;
        }
        
        if (conversationsData.length === 0) {
          setUnreadMessageCount(0);
          return;
        }
        
        // Extract conversation IDs
        const conversationIds = conversationsData.map(conv => conv.id);
        
        // Get count of unread messages in these conversations
        const { data: unreadData, error: unreadError } = await supabase
          .from('messages')
          .select('id', { count: 'exact' })
          .in('conversation_id', conversationIds)
          .eq('is_read', false)
          .neq('sender_id', user.id);
        
        if (unreadError) {
          console.error("Error fetching unread message count:", unreadError);
          return;
        }
        
        setUnreadMessageCount(unreadData?.length || 0);
      } catch (error) {
        console.error("Error in fetchUnreadMessageCount:", error);
      }
    };
    
    fetchUnreadMessageCount();
    
    // Set up a real-time subscription for new messages
    const subscription = supabase
      .channel('messages-changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages'
      }, (payload) => {
        // Check if the message is for this user and not from this user
        const newMsg = payload.new as any;
        
        if (newMsg.sender_id !== user.id) {
          // Check if the message is in one of the user's conversations
          supabase
            .from('conversations')
            .select('id')
            .eq('id', newMsg.conversation_id)
            .eq('mentee_id', user.id)
            .single()
            .then(({ data }) => {
              if (data) {
                // Increment the unread count
                setUnreadMessageCount(prev => prev + 1);
              }
            });
        }
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [user?.id]);
  
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 mb-8 sm:mb-10">
      <ActionCard 
        icon={<Users className="h-4 w-4 sm:h-5 sm:w-5 text-emu-blue" />}
        title="Search Mentors"
        route="/search-mentors"
        onNavigate={onNavigate}
      />
      
      <ActionCard 
        icon={<Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-emu-blue" />}
        title="Calendar"
        route="/mentee-calendar"
        onNavigate={onNavigate}
      />
      
      <ActionCard 
        icon={<User className="h-4 w-4 sm:h-5 sm:w-5 text-emu-blue" />}
        title="My Profile"
        route={`/mentee-profile/${user?.id || ''}`}
        onNavigate={onNavigate}
      />
      
      <ActionCard 
        icon={<MessageCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emu-blue" />}
        title="Messages"
        route="/messages"
        onNavigate={onNavigate}
        badge={unreadMessageCount > 0 ? (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-[10px] sm:text-xs">
            {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
          </span>
        ) : null}
      />
    </div>
  );
};

export default MenteeDashboardActions;
